import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Users, Video, Star, Settings2,
  ShieldCheck, Network, CloudCog, Bug,
  KeyRound, ClipboardList, Brain, Siren,
  Building, Laptop, Layers,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Users, value: "4000+", label: "Professionals Upskilled", desc: "Security professionals trained across cybersecurity, cloud, infrastructure, and compliance domains." },
  { icon: Video, value: "500+", label: "Instructor-Led Workshops", desc: "Hands-on cybersecurity sessions led by experienced industry practitioners." },
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Consistently recognized for delivering engaging learning experiences and measurable outcomes." },
  { icon: Settings2, value: "100%", label: "Customized Learning Programs", desc: "Training aligned with organizational security policies, compliance requirements, and business objectives." }
];

const LEARNING_OUTCOMES = [
  { icon: ShieldCheck, title: "Cybersecurity Fundamentals", desc: "Establish a strong security foundation by learning cyber threats, attack vectors, security principles, and essential practices for protecting enterprise systems and digital assets." },
  { icon: Network, title: "Network Security", desc: "Secure enterprise networks through firewall management, intrusion detection, VPNs, network segmentation, secure communication protocols, and continuous network monitoring." },
  { icon: CloudCog, title: "Cloud Security", desc: "Protect cloud environments across AWS, Microsoft Azure, and Google Cloud with identity and access management, workload protection, encryption, and compliance best practices." },
  { icon: Bug, title: "Ethical Hacking & Penetration Testing", desc: "Identify vulnerabilities using ethical hacking methodologies, penetration testing techniques, reconnaissance, vulnerability assessments, and security validation exercises." },
  { icon: KeyRound, title: "Cryptography & Data Protection", desc: "Understand modern cryptographic techniques, encryption standards, digital certificates, PKI, hashing, and secure data protection for enterprise applications." },
  { icon: ClipboardList, title: "Enterprise Risk Management", desc: "Assess cyber risks, strengthen governance, support regulatory compliance, and implement security controls that align with organizational risk management objectives." },
  { icon: Brain, title: "AI Cybersecurity", desc: "Explore AI-powered threat detection, automated incident response, behavioral analytics, and intelligent security operations to strengthen enterprise cyber defense." },
  { icon: Siren, title: "Security Operations & Incident Response", desc: "Develop practical skills in security monitoring, threat intelligence, incident response, digital forensics, and recovery strategies to minimize the impact of cyber threats." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive online sessions with cyber labs, attack simulations, and expert-led demonstrations." },
  { icon: Building, title: "Classroom Training", desc: "Instructor-led workshops featuring practical security exercises and enterprise case studies." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "On-demand access to cybersecurity courses, assessments, recorded sessions, and learning resources." },
  { icon: Layers, title: "Blended Learning", desc: "A combination of instructor-led sessions, digital learning, hands-on labs, and practical security assessments." }
];

const BY_THE_NUMBERS = [
  { value: "50+", label: "Fortune & High-Growth Companies Served" },
  { value: "25+", label: "Global Technology Partners" },
  { value: "200+", label: "Corporate Learning Programs" },
  { value: "100+", label: "Enterprise Trainers" }
];

const CybersecurityPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE CYBERSECURITY LEARNING"
        title="Enterprise Cybersecurity Training for Modern Security Teams"
        subtitle="Strengthen your organization's security posture with Skillsgain's enterprise Cyber Security Course portfolio. Cover essential areas including Network Security, Cloud Security, Ethical Hacking, Enterprise Risk Management, AI Cybersecurity, and Cybersecurity with AI through instructor-led learning, practical labs, and enterprise use cases."
      />

      {/* Enterprise Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">ENTERPRISE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Proven outcomes across enterprise cybersecurity learning</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Cybersecurity Capabilities */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CYBERSECURITY CAPABILITIES</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Practical cybersecurity skills for enterprise environments</h2>
            <p className="mt-3 text-gray-600">Develop in-demand security expertise through instructor-led training, hands-on labs, real-world attack simulations, and enterprise security scenarios.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Experiences */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">LEARNING EXPERIENCES</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Learning formats designed for enterprise teams</h2>
              <p className="mt-3 text-gray-600">Choose instructor-led, classroom, self-paced, or blended learning experiences that align with organizational security initiatives and compliance requirements.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#F57C20] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain delivered a customized enterprise cybersecurity training program covering cloud security, ethical hacking, and enterprise risk management. The hands-on labs and practical scenarios significantly improved our team's confidence in identifying threats, responding to incidents, and strengthening organizational security.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR SECURITY & IT LEADERS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Strengthen Enterprise Cybersecurity Capabilities</h2>
                <p className="mt-3 text-gray-300">Prepare security, cloud, and IT teams with enterprise training covering Cyber Security, Network Security, Cloud Security, Ethical Hacking, Enterprise Risk Management, and emerging AI Cybersecurity practices.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Security Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default CybersecurityPage;
